import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/utils/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Plus, Eye, TrendingUp, Wallet, User, Lock, Mail, Phone, Edit2, X, Save, MessageCircle, Send, Zap, Clock } from "lucide-react";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [myAds, setMyAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [editForm, setEditForm] = useState({ contact_phone: "", contact_whatsapp: "", contact_telegram: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [autoBumpAd, setAutoBumpAd] = useState(null);
  const [autoBumpInterval, setAutoBumpInterval] = useState("6");
  const [savingAutoBump, setSavingAutoBump] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect agents to their panel - they don't have advertiser dashboard
    if (user?.role === 'agent') {
      navigate('/agent');
      return;
    }
    loadMyAds();
  }, [user, navigate]);

  const loadMyAds = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/my-ads");
      setMyAds(response.data || []);
    } catch (error) {
      console.error("Failed to load ads:", error);
      console.error("Error details:", error.response?.data);
      setError(error.response?.data?.detail || "Failed to load ads");
      setMyAds([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.old_password || !passwordForm.new_password) {
      toast.error("Please fill all fields");
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password
      });
      toast.success("Password changed successfully!");
      setPasswordForm({ old_password: "", new_password: "", confirm_password: "" });
      setShowProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleBumpAd = async (adId) => {
    try {
      await api.post(`/ads/${adId}/bump`);
      toast.success("Ad bumped successfully!");
      loadMyAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bump ad");
    }
  };

  const handleEditAd = (ad) => {
    setEditingAd(ad.id);
    setEditForm({
      contact_phone: ad.contact_phone || "",
      contact_whatsapp: ad.contact_whatsapp || "",
      contact_telegram: ad.contact_telegram || ""
    });
  };

  const handleCancelEdit = () => {
    setEditingAd(null);
    setEditForm({ contact_phone: "", contact_whatsapp: "", contact_telegram: "" });
  };


  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [changingEmail, setChangingEmail] = useState(false);

  const handleChangeEmail = async (e) => {
    e.preventDefault();

    const newEmail = (emailForm.new_email || "").trim();
    const currentPassword = (emailForm.current_password || "").trim();

    if (!newEmail || !currentPassword) {
      toast.error("Please fill all fields");
      return;
    }

    setChangingEmail(true);
    try {
      await api.post("/account/change-email", {
        new_email: newEmail,
        current_password: currentPassword,
      });
      toast.success("Email updated successfully. Please log in again.");
      setEmailForm({ new_email: "", current_password: "" });
      logout();
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update email");
    } finally {
      setChangingEmail(false);
    }
  };

  const handleSaveEdit = async (adId) => {
    setSavingEdit(true);
    try {
      await api.patch(`/ads/${adId}`, editForm);
      toast.success("Ad updated successfully!");
      setEditingAd(null);
      loadMyAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update ad");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleOpenAutoBump = (ad) => {
    setAutoBumpAd(ad);
    setAutoBumpInterval(ad.auto_bump_interval?.toString() || "6");
  };

  const handleCloseAutoBump = () => {
    setAutoBumpAd(null);
    setAutoBumpInterval("6");
  };

  const handleToggleAutoBump = async (enabled) => {
    if (!autoBumpAd) return;
    setSavingAutoBump(true);
    try {
      await api.post(`/ads/${autoBumpAd.id}/auto-bump`, {
        ad_id: autoBumpAd.id,
        enabled: enabled,
        interval_hours: parseInt(autoBumpInterval)
      });
      toast.success(enabled ? "Auto-bump enabled!" : "Auto-bump disabled!");
      setAutoBumpAd(null);
      loadMyAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to configure auto-bump. You may need an active subscription.");
    } finally {
      setSavingAutoBump(false);
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm("Delete this ad? This cannot be undone.")) return;

    try {
      await api.post(`/ads/${adId}/delete`);
      toast.success("Ad deleted");
      loadMyAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete ad");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Header />

        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Header />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-white mb-1 sm:mb-2" style={{fontFamily: 'Inter, sans-serif'}}>
            Welcome, {user?.name}
          </h1>
          <p className="text-gray-400 text-sm sm:text-base">Manage your ads and account</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card className="p-4 sm:p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm">Total Ads</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">{myAds.length}</p>
              </div>
              <Eye className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400" />
            </div>
          </Card>

          <Card className="p-4 sm:p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm">Total Views</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  {myAds.reduce((sum, ad) => sum + (ad.views || 0), 0)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-green-400" />
            </div>
          </Card>

          <Card className="p-4 sm:p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs sm:text-sm">Wallet Balance</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">₹{user?.wallet_balance || 0}</p>
              </div>
              <Wallet className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" />
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4 mb-6 sm:mb-8">
          <Button
            data-testid="post-new-ad-button"
            onClick={() => navigate('/post-ad')}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 mr-1 sm:mr-2" />
            Post New Ad
          </Button>
          <Button
            data-testid="manage-wallet-button"
            onClick={() => navigate('/wallet')}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10 text-sm sm:text-base"
          >
            <Wallet className="w-4 h-4 mr-1 sm:mr-2" />
            Manage Wallet
          </Button>
          <Button
            data-testid="profile-button"
            onClick={() => setShowProfile(!showProfile)}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10 col-span-2 sm:col-span-1 text-sm sm:text-base"
          >
            <User className="w-4 h-4 mr-1 sm:mr-2" />
            Profile
          </Button>
        </div>

        {/* Profile Section */}
        {showProfile && (
          <Card className="p-4 sm:p-6 bg-white/5 backdrop-blur-sm border-white/10 mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
              <User className="w-5 h-5 sm:w-6 sm:h-6" />
              My Profile
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {/* Profile Info */}
              <div className="space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Account Information</h3>
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center gap-3 p-2 sm:p-3 bg-white/5 rounded-lg">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-gray-400 text-xs">Name</p>
                      <p className="text-white text-sm sm:text-base truncate">{user?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-2 sm:p-3 bg-white/5 rounded-lg">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-gray-400 text-xs">Email</p>
                      <p className="text-white text-sm sm:text-base truncate">{user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-2 sm:p-3 bg-white/5 rounded-lg">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-gray-400 text-xs">Mobile</p>
                      <p className="text-white text-sm sm:text-base truncate">{user?.mobile}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Change Password Form */}
              <div className="space-y-6 sm:space-y-8">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
                    <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                    Change Password
                  </h3>
                  <form onSubmit={handleChangePassword} className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">Current Password *</label>
                      <Input
                        type="password"
                        placeholder="Enter current password"
                        value={passwordForm.old_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 text-sm"
                        data-testid="current-password-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">New Password *</label>
                      <Input
                        type="password"
                        placeholder="Enter new password (min 6 chars)"
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 text-sm"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">Confirm New Password *</label>
                      <Input
                        type="password"
                        placeholder="Confirm new password"
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 text-sm"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={changingPassword}
                      className="w-full bg-green-600 hover:bg-green-700 text-white text-sm sm:text-base"
                      data-testid="change-password-submit"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      {changingPassword ? "Changing..." : "Change Password"}
                    </Button>
                  </form>
                </div>

                {/* Change Email Form */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                    Change Email
                  </h3>
                  <form onSubmit={handleChangeEmail} className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">New Email *</label>
                      <Input
                        type="email"
                        placeholder="Enter new email"
                        value={emailForm.new_email}
                        onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2">Current Password *</label>
                      <Input
                        type="password"
                        placeholder="Enter current password"
                        value={emailForm.current_password}
                        onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 text-sm"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={changingEmail}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {changingEmail ? "Updating..." : "Change Email"}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* My Ads */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6">My Ads</h2>
          {error ? (
            <Card className="p-6 sm:p-12 bg-white/5 backdrop-blur-sm border-white/10 text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <Button
                onClick={loadMyAds}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Try Again
              </Button>
            </Card>
          ) : myAds.length === 0 ? (
            <Card className="p-6 sm:p-12 bg-white/5 backdrop-blur-sm border-white/10 text-center">
              <p className="text-gray-400 mb-4">You haven't posted any ads yet</p>
              <Button
                onClick={() => navigate('/post-ad')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Post Your First Ad
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              {myAds.map((ad) => (
                <Card key={ad.id} className="p-4 sm:p-6 bg-white/5 backdrop-blur-sm border-white/10 overflow-hidden">
                  {/* Mobile Layout: Stack vertically */}
                  <div className="flex flex-col">
                    {/* Header: Title + Status */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <h3 className="text-lg sm:text-xl font-semibold text-white break-words">{ad.title}</h3>
                      <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                        ad.status === 'approved' ? 'bg-green-600/20 text-green-400' :
                        ad.status === 'pending' ? 'bg-yellow-600/20 text-yellow-400' :
                        ad.status === 'rejected' ? 'bg-red-600/20 text-red-400' :
                          'bg-gray-600/20 text-gray-400'
                      }`}>
                        {ad.status}
                      </span>
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-400 text-sm sm:text-base mb-4 line-clamp-2 break-words">{ad.description}</p>
                    
                    {/* Edit Mode */}
                    {editingAd === ad.id ? (
                      <div className="bg-white/5 rounded-lg p-3 sm:p-4 mb-4 border border-white/10">
                        <h4 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm sm:text-base">
                          <Edit2 className="w-4 h-4 flex-shrink-0" /> Edit Contact Information
                        </h4>
                        <div className="grid grid-cols-1 gap-3 sm:gap-4">
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              <Phone className="w-3 h-3 inline mr-1" /> Phone Number
                            </label>
                            <Input
                              type="tel"
                              value={editForm.contact_phone}
                              onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                              placeholder="9876543210"
                              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              <MessageCircle className="w-3 h-3 inline mr-1" /> WhatsApp Number
                            </label>
                            <Input
                              type="tel"
                              value={editForm.contact_whatsapp}
                              onChange={(e) => setEditForm({ ...editForm, contact_whatsapp: e.target.value })}
                              placeholder="919876543210"
                              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              <Send className="w-3 h-3 inline mr-1" /> Telegram Number
                            </label>
                            <Input
                              type="tel"
                              value={editForm.contact_telegram}
                              onChange={(e) => setEditForm({ ...editForm, contact_telegram: e.target.value })}
                              placeholder="919876543210"
                              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4">
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(ad.id)}
                            disabled={savingEdit}
                            className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none"
                          >
                            <Save className="w-4 h-4 mr-1" />
                            {savingEdit ? "Saving..." : "Save"}
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleCancelEdit}
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/10 flex-1 sm:flex-none"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Display Contact Info */
                      <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400 mb-4">
                        {ad.contact_phone && (
                          <span className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded">
                            <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" /> 
                            <span className="truncate">{ad.contact_phone}</span>
                          </span>
                        )}
                        {ad.contact_whatsapp && (
                          <span className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded">
                            <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 flex-shrink-0" /> 
                            <span className="truncate">{ad.contact_whatsapp}</span>
                          </span>
                        )}
                        {ad.contact_telegram && (
                          <span className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded">
                            <Send className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400 flex-shrink-0" /> 
                            <span className="truncate">{ad.contact_telegram}</span>
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Stats Grid - 2 cols on mobile, 4 on desktop */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm mb-4">
                      <div className="bg-white/5 rounded-lg p-2 sm:p-3">
                        <p className="text-gray-500 text-xs">Views</p>
                        <p className="text-white font-semibold">{ad.views || 0}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 sm:p-3">
                        <p className="text-gray-500 text-xs">WhatsApp</p>
                        <p className="text-white font-semibold">{ad.whatsapp_clicks || 0}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 sm:p-3">
                        <p className="text-gray-500 text-xs">Calls</p>
                        <p className="text-white font-semibold">{ad.call_clicks || 0}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 sm:p-3">
                        <p className="text-gray-500 text-xs">Top-ups</p>
                        <p className="text-white font-semibold">{ad.top_up_count || 0}</p>
                      </div>
                    </div>
                    
                    {/* Action Buttons - horizontal on mobile */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/ad/${ad.id}`)}
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10 flex-1 sm:flex-none"
                        data-testid={`view-ad-${ad.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      {editingAd !== ad.id && (
                        <Button
                          size="sm"
                          onClick={() => handleEditAd(ad)}
                          variant="outline"
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 flex-1 sm:flex-none"
                          data-testid={`edit-ad-${ad.id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      )}
                      {ad.status === 'approved' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleBumpAd(ad.id)}
                            className="bg-purple-600 hover:bg-purple-700 text-white flex-1 sm:flex-none"
                            data-testid={`bump-ad-${ad.id}`}
                          >
                            Bump
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleOpenAutoBump(ad)}
                            variant="outline"
                            className={`flex-1 sm:flex-none ${
                              ad.auto_bump_enabled 
                                ? 'border-green-500/50 text-green-400 hover:bg-green-500/10' 
                                : 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10'
                            }`}
                            data-testid={`auto-bump-ad-${ad.id}`}
                          >
                            <Zap className="w-4 h-4 mr-1" />
                            {ad.auto_bump_enabled ? 'Auto ON' : 'Auto'}
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        onClick={() => handleDeleteAd(ad.id)}
                        variant="outline"
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10 flex-1 sm:flex-none"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Auto-Bump Configuration Modal */}
      {autoBumpAd && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6 bg-slate-900 border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Auto-Bump Settings
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCloseAutoBump}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <p className="text-white font-medium mb-1 line-clamp-1">{autoBumpAd.title}</p>
                <p className="text-gray-400 text-sm">
                  Status: {autoBumpAd.auto_bump_enabled ? (
                    <span className="text-green-400">Auto-bump active</span>
                  ) : (
                    <span className="text-gray-500">Not active</span>
                  )}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Bump Interval
                </label>
                <Select value={autoBumpInterval} onValueChange={setAutoBumpInterval}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select interval" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">Every 3 hours</SelectItem>
                    <SelectItem value="6">Every 6 hours</SelectItem>
                    <SelectItem value="12">Every 12 hours</SelectItem>
                    <SelectItem value="24">Every 24 hours</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-gray-500 text-xs mt-1">Your ad will automatically bump at this interval</p>
              </div>
              
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm">
                  <strong>Note:</strong> Auto-bump requires an active subscription plan.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                {autoBumpAd.auto_bump_enabled ? (
                  <Button
                    onClick={() => handleToggleAutoBump(false)}
                    disabled={savingAutoBump}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {savingAutoBump ? "Saving..." : "Disable Auto-Bump"}
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleToggleAutoBump(true)}
                    disabled={savingAutoBump}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {savingAutoBump ? "Saving..." : "Enable Auto-Bump"}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleCloseAutoBump}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Dashboard;
